import YoutubeVideoModal from "@components/YoutubeVideoModal";
import { isJSXElement } from "@utils/common";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { FaPlay } from "react-icons/fa6";
import { GoCheckCircleFill } from "react-icons/go";
import ZohoFormModal from "./ZohoFormModal";

export interface IndividualWebinarProps {
    title: string;
    listHeading: string | JSX.Element;
    listText: { label: string }[];
    mediaUrl: string;
    speakerList: { image: string; name: string; role: string }[];
    youtubeURL: string;
    inThisWebinar: string;
    webinarSlug: string;
}

const TTL_DAYS = 60;

function grantVideoAccess(accessKey: string) {
    const expiresAt = Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(accessKey, expiresAt.toString());
}

function hasVideoAccess(accessKey: string): boolean {
    if (typeof window === "undefined") return false;
    const expiresAt = localStorage.getItem(accessKey);
    return expiresAt ? Date.now() < parseInt(expiresAt) : false;
}

const IndividualWebinar: React.FC<IndividualWebinarProps> = (props) => {
    const { title, listText, listHeading, speakerList, mediaUrl, youtubeURL, inThisWebinar, webinarSlug } = props;
    const [showYoutubeModal, setShowYoutubeModal] = useState(false);
    const [showZohoFormModal, setShowZohoFormModal] = useState(false);
    const [zohoFormDisabled, setZohoFormDisabled] = useState(false);
    const ACCESS_KEY = `${webinarSlug}_access_granted`;

    const handlePlayClick = () => {
        if (hasVideoAccess(ACCESS_KEY)) {
            setShowYoutubeModal(true);
        } else setShowZohoFormModal(true);
    };

    const handleZohoFormSubmit = () => {
        setShowZohoFormModal(false);
        setShowYoutubeModal(true);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const formSubmitted = urlParams.get("form_submitted");

        if (formSubmitted === "true") {
            window.history.replaceState(null, "", window.location.pathname);
            grantVideoAccess(ACCESS_KEY);
            setShowZohoFormModal(false);
            setShowYoutubeModal(true);
        } else if (hasVideoAccess(ACCESS_KEY)) {
            setZohoFormDisabled(true);
        }
    }, [ACCESS_KEY]);

    return (
        <section className="lg:max-w-7xl mx-auto lg:mx-auto lg:px-4 px-4 lg:pt-16 pt-8 flex flex-col gap-11 lg:my-16 mb-10 md:mb-0">
            <div className="flex lg:flex-row flex-col">
                <div className="flex flex-col items-center gap-8 md:w-2/5">
                    <h1 className={"text-black md:text-7xl text-4xl font-bold text-gray-900 desktop-line-height"} style={{ fontFamily: "Degular Variable" }}>
                        {title}
                    </h1>
                    <div className="md:hidden flex justify-end items-start">
                        <div className="relative">
                            <Image src={mediaUrl} alt="webinar-cover" className="rounded-lg" height={720} width={634} style={{ objectFit: "contain" }} />
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="relative w-24 h-24 cursor-pointer" onClick={handlePlayClick}>
                                    <div className="absolute inset-0 rounded-full bg-blue-color opacity-75 animate-ping"></div>
                                    <div className="relative w-full h-full rounded-full bg-white flex items-center justify-center">
                                        <FaPlay className="text-blue-color w-10 h-10 ml-1" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-7">
                        {isJSXElement(listHeading) ? listHeading : <p className="text-gray-700 lg:text-base text-xl font-normal lg:text-left text-left">{listHeading}</p>}
                        <p className="text-gray-700 lg:text-base text-xl font-normal lg:text-left text-left">{inThisWebinar}</p>
                        <div className="flex flex-col gap-4">
                            {listText.map((text, index) => (
                                <div key={index} className="flex gap-2">
                                    <span className="flex-shrink-0 mt-0.5">
                                        <GoCheckCircleFill size="20px" color="blue" />
                                    </span>
                                    <p className="text-base text-gray-700">{text.label}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-2 mt-8">
                            <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-2">Speakers</h3>
                            <div className="flex gap-12 mt-2">
                                {speakerList.map((speaker, index) => (
                                    <div key={index} className="flex flex-col gap-4">
                                        <Image src={speaker.image} height={78} width={78} className="rounded-full" alt="speaker image" />
                                        <div>
                                            <p className="text-xl font-semibold">{speaker.name}</p>
                                            <p className="text-md text-blue-color">{speaker.role}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hidden w-3/5 md:flex justify-end items-start">
                    <div className="relative" style={{ height: "332px", width: "634px" }}>
                        <Image src={mediaUrl} alt="webinar-cover" className="rounded-lg" height={720} width={634} style={{ objectFit: "contain" }} />
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative w-24 h-24 cursor-pointer" onClick={handlePlayClick}>
                                <div className="absolute inset-0 rounded-full bg-blue-color opacity-75 animate-ping"></div>
                                <div className="relative w-full h-full rounded-full bg-white flex items-center justify-center">
                                    <FaPlay className="text-blue-color w-10 h-10 ml-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {!zohoFormDisabled && showZohoFormModal && <ZohoFormModal onClose={() => setShowZohoFormModal(false)} onSuccess={handleZohoFormSubmit} webinarSlug={webinarSlug} />}

            {showYoutubeModal && <YoutubeVideoModal isOpen={showYoutubeModal} onClose={() => setShowYoutubeModal(false)} src={youtubeURL} />}
        </section>
    );
};

export default IndividualWebinar;
